'use client';

import deepmerge from 'deepmerge';
import React, { createContext, useContext } from 'react';
import useComposerReadyState from '~/hooks/common/composer/useComposerReadyState';
import type { DeepPartial } from '~/types';

export type ContextType = {
  isComposerReady: boolean;
  showComposerTreatment: boolean;
};

export const defaultContext: ContextType = {
  isComposerReady: false,
  showComposerTreatment: false
};

const ComposerContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  showComposerTreatment: boolean;
}>;

export function ComposerContextProvider({
  children,
  showComposerTreatment
}: OwnProps) {
  const isComposerReady = useComposerReadyState();

  return (
    <ComposerContext.Provider
      value={{ isComposerReady, showComposerTreatment }}
    >
      {children}
    </ComposerContext.Provider>
  );
}

ComposerContextProvider.displayName = 'ComposerContextProvider';

export const generateComposerContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useIsComposerReady() {
  return useContext(ComposerContext).isComposerReady;
}

export function useShowComposerTreatment() {
  return useContext(ComposerContext).showComposerTreatment;
}

export function MockedComposerContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <ComposerContext.Provider value={generateComposerContext(mockValue)}>
      {children}
    </ComposerContext.Provider>
  );
}

export default ComposerContext;
